/**
 * Canonical sidebar and global-create menu definitions with stable keys.
 * Icons for the sidebar rail are resolved in MainMenu via SIDEBAR_ICON_KEYS.
 */

export const SIDEBAR_MODULE_KEYS = [
  "reports",
  "orders",
  "shipments",
  "inventory",
  "finance",
  "sales",
  "production",
  "planner",
  "master",
  "integrations",
];

/** @typedef {{ key: string, name: string, url: string, group?: string, kind?: string }} SidebarChild */

/** @type {Record<string, { label: string, url: string, commonUrl: string | string[], children: SidebarChild[] }>} */
export const SIDEBAR_CATALOG_BY_KEY = {
  reports: {
    label: "Reports",
    url: "#",
    commonUrl: ["dashboard", "reports"],
    children: [
      { key: "reports.dashboard", name: "Dashboard", url: "/dashboard" },
      { key: "reports.inventory", name: "Inventory", url: "/reports/inventory" },
      { key: "reports.purchases", name: "Purchases", url: "/reports/purchase" },
      { key: "reports.transfers", name: "Transfers", url: "/reports/transfer" },
      { key: "reports.shipments", name: "Shipments", url: "/reports/shipment" },
    ],
  },
  inventory: {
    label: "Inventory",
    url: "#",
    commonUrl: ["inventory", "replenishments", "receipts", "receipt_adjustment"],
    children: [
      { key: "inventory.summary", name: "Summary", url: "/inventory" },
      { key: "inventory.details", name: "Details", url: "/inventory/details" },
      { key: "inventory.transactions", name: "Transactions", url: "/inventory/transactions" },
      {
        key: "inventory.location_replenishment",
        name: "Location Replenishment",
        url: "/replenishments",
      },
      { key: "inventory.receipts", name: "Receipts", url: "/receipts/" },
      {
        key: "inventory.receipt_adjustments",
        name: "Receipt Adjustments",
        url: "/receipt_adjustment",
      },
    ],
  },
  sales: {
    label: "Sales",
    url: "#",
    commonUrl: [
      "sales",
      "promo",
      "customer_return",
      "config_portal",
      "customers",
      "customer_segments",
    ],
    children: [
      { key: "sales.customers", name: "Customers", url: "/master/customers" },
      { key: "sales.quote", name: "Quotes", url: "/sales/quotation" },
      { key: "sales.sales_orders", name: "Sales Orders", url: "/sales/order" },
      { key: "sales.price_lists", name: "Price Lists", url: "/sales/pricelist" },
      { key: "sales.customer_segments", name: "Customer Segments", url: "/master/customer_segments" },
      { key: "sales.promotions", name: "Promotions", url: "/promos" },
      { key: "sales.return", name: "Returns", url: "/customer_return" },
      { key: "sales.b2b_portals", name: "B2B Portals", url: "/config_portals" },
    ],
  },
  shipments: {
    label: "Shipments",
    url: "#",
    commonUrl: ["shipment", "delivery", "stock_packs", "picklists"],
    children: [
      {
        key: "shipments.po_to",
        name: "Inbound Shipments",
        url: "/shipment/po-to",
        group: "Purchase & Transfer",
      },
      { key: "shipments.asn", name: "ASN", url: "/shipment/asn", group: "Purchase & Transfer" },
      { key: "shipments.picking_lists", name: "Picking Lists", url: "/picklists", group: "Sales" },
      { key: "shipments.packs", name: "Packs", url: "/stock_packs", group: "Sales" },
      { key: "shipments.deliveries", name: "Deliveries", url: "/delivery", group: "Sales" },
      { key: "shipments.sales", name: "Sales Shipments", url: "/shipment", group: "Sales" },
      { key: "shipments.legs", name: "Shipment Legs", url: "/shipment/leg", group: "Sales" },
    ],
  },
  finance: {
    label: "Finance",
    url: "#",
    commonUrl: [
      "invoice",
      "bill",
      "charts_of_accounts",
      "tax_categories",
      "tax_rates",
      "product_tax_classifications",
      "prepayment",
      "payments",
      "supplier_credit_note",
      "customer_credit_note",
      "charge-type",
    ],
    children: [
      {
        key: "finance.sales_invoices",
        name: "Sales Invoices",
        url: "/sale_invoice",
        group: "Receivables",
      },
      {
        key: "finance.customer_credits",
        name: "Customer Credits",
        url: "/customer_credit_notes",
        group: "Receivables",
      },
      { key: "finance.bills", name: "Bills", url: "/bills", group: "Payables" },
      {
        key: "finance.supplier_credits",
        name: "Supplier Credits",
        url: "/supplier_credit_notes",
        group: "Payables",
      },
      { key: "finance.payments", name: "Payments", url: "/payments", group: "Settlement" },
      {
        key: "finance.accounts",
        name: "Accounts",
        url: "/master/charts_of_accounts",
        group: "Setup",
      },
      {
        key: "finance.tax_categories",
        name: "Tax Categories",
        url: "/master/tax_categories",
        group: "Tax",
      },
      { key: "finance.tax_rates", name: "Tax Rates", url: "/master/tax_rates", group: "Tax" },
      {
        key: "finance.product_tax_class",
        name: "Product Tax Class",
        url: "/master/product_tax_classifications",
        group: "Tax",
      },
      {
        key: "finance.charge_type",
        name: "Charge Type",
        url: "/master/charge-type",
        group: "Setup",
      },
    ],
  },
  production: {
    label: "Production",
    url: "#",
    commonUrl: ["bom", "manufacturing-order"],
    children: [
      { key: "production.bom", name: "Bill of Materials", url: "/bom" },
      { key: "production.manufacturing_orders", name: "Manufacturing Orders", url: "/manufacturing-order" },
      { key: "production.co_manf_order", name: "Co-Manf Order", url: "/co-manufacturing-order" },
    ],
  },
  planner: {
    label: "Planner",
    url: "#",
    commonUrl: "planner",
    children: [
      { key: "planner.planned_orders", name: "Planned Orders", url: "/planner/planned_order" },
      { key: "planner.projected_stocks", name: "Projected Stocks", url: "/planner/projected_stock" },
    ],
  },
  orders: {
    label: "Orders",
    url: "#",
    commonUrl: [
      "orders",
      "transfer_orders",
      "transfer_order",
      "allocation",
      "sales/order",
    ],
    children: [
      { key: "orders.purchase_orders", name: "Purchase Orders", url: "/orders/purchase" },
      { key: "orders.sales_orders", name: "Sales Orders", url: "/sales/order" },
      { key: "orders.transfer_orders", name: "Transfer Orders", url: "/transfer_orders" },
      { key: "orders.allocations", name: "Allocations", url: "/orders/allocation" },
    ],
  },
  master: {
    label: "Master",
    url: "/master",
    commonUrl: "master",
    children: [],
  },
  integrations: {
    label: "Integrations",
    url: "/integration",
    commonUrl: "integration",
    children: [],
  },
};

export const SIDEBAR_CATALOG = SIDEBAR_MODULE_KEYS.map(key => ({
  key,
  ...SIDEBAR_CATALOG_BY_KEY[key],
}));

/** Labels for the Active Modules section on /organization. */
export const ACTIVE_MODULE_OPTIONS = SIDEBAR_MODULE_KEYS.map(key => ({
  key,
  label: SIDEBAR_CATALOG_BY_KEY[key].label,
}));

/** Default sidebar module for each global-create group key. */
export const GLOBAL_CREATE_GROUP_MODULE = {
  "create.general": "master",
  "create.inventory": "inventory",
  "create.shipments": "shipments",
  "create.orders": "orders",
  "create.finance": "finance",
  "create.sales": "sales",
  "create.production": "production",
};

/** Per-item overrides when a create item belongs to a different module than its group. */
export const GLOBAL_CREATE_ITEM_MODULE = {
  "create.general.forecast": "planner",
};

/** @type {{ key: string, label: string, icon: string, children: { key: string, name: string, link: string }[] }[]} */
export const GLOBAL_CREATE_CATALOG = [
  {
    key: "create.general",
    label: "GENERAL",
    icon: "GeneralMenuIcon",
    children: [
      { key: "create.general.product", name: "Product", link: "/master/product/create" },
      { key: "create.general.product_plan", name: "Product Plan", link: "/master/product_plan/create" },
      { key: "create.general.supplier", name: "Supplier", link: "/master/supplier/create" },
      { key: "create.general.forecast", name: "Forecast", link: "/master/forecast/create" },
    ],
  },
  {
    key: "create.inventory",
    label: "INVENTORY",
    icon: "InventoryMenuIcon",
    children: [
      {
        key: "create.inventory.stock_adjustment",
        name: "Stock Adjustment",
        link: "/master/stock_line_adjust/create",
      },
      {
        key: "create.inventory.adjustment_reason",
        name: "Adjustment Reason",
        link: "/master/transaction_reason/create",
      },
      { key: "create.inventory.stock_zone", name: "Stock Zone", link: "/master/stock_zone/create" },
      {
        key: "create.inventory.stock_location",
        name: "Stock Location",
        link: "/master/stock_location/create",
      },
    ],
  },
  {
    key: "create.shipments",
    label: "SHIPMENTS",
    icon: "ShipmentMenuIcon",
    children: [{ key: "create.shipments.shipment", name: "Shipment", link: "/shipment/create" }],
  },
  {
    key: "create.orders",
    label: "ORDERS",
    icon: "OrderMenuIcon",
    children: [
      { key: "create.orders.purchase_order", name: "Purchase Order", link: "/orders/purchase/create" },
      { key: "create.orders.sales_order", name: "Sales Order", link: "/sales/order/create" },
      { key: "create.orders.transfer_order", name: "Transfer Order", link: "/transfer_order/create" },
    ],
  },
  {
    key: "create.finance",
    label: "FINANCE",
    icon: "FinanceIcon",
    children: [
      { key: "create.finance.bill", name: "Bill", link: "/bills/create_general_bill" },
      { key: "create.finance.credit", name: "Credit", link: "/supplier_credit_note/create" },
    ],
  },
  {
    key: "create.sales",
    label: "SALES",
    icon: "SaleIcon",
    children: [
      { key: "create.sales.customers", name: "Customers", link: "/master/customer/create" },
      { key: "create.sales.quotes", name: "Quotes", link: "/sales/quotation/create" },
      { key: "create.sales.sales_orders", name: "Sales Orders", link: "/sales/order/create" },
      { key: "create.sales.pricelist", name: "Pricelist", link: "/sales/pricelist" },
      {
        key: "create.sales.customer_segment",
        name: "Customer Segment",
        link: "/master/customer_segment/create",
      },
    ],
  },
  {
    key: "create.production",
    label: "PRODUCTION",
    icon: "SaleIcon",
    children: [{ key: "create.production.bom", name: "Bill of Materials", link: "/bom" }],
  },
];

export function getSidebarCatalogForPathMatch() {
  return SIDEBAR_CATALOG;
}

export function pathnameMatchesModule(pathname, moduleEntry) {
  const { commonUrl } = moduleEntry;
  if (Array.isArray(commonUrl)) {
    return commonUrl.some(url => pathname.includes(url));
  }
  return pathname.includes(commonUrl);
}

/** @param {string} path */
function normalizeNavPath(path) {
  if (!path || path === "#") {
    return null;
  }
  if (path === "/") {
    return "/";
  }
  return path.endsWith("/") ? path.slice(0, -1) : path;
}

/**
 * @param {string} pathname
 * @param {string} childUrl
 * @returns {boolean}
 */
export function pathnameMatchesChildUrl(pathname, childUrl) {
  const base = normalizeNavPath(childUrl);
  if (!base || base === "/") {
    return false;
  }
  const path = normalizeNavPath(pathname) ?? pathname;
  if (path === base) {
    return true;
  }
  return path.startsWith(`${base}/`);
}

/**
 * @param {string} pathname
 * @param {Array<{ key: string, commonUrl?: string | string[], children?: SidebarChild[] }>} modules
 * @returns {{ moduleKey: string, childKey: string | null }}
 */
export function findActiveNavigation(pathname, modules) {
  /** @type {{ moduleKey: string, childKey: string, urlLength: number, commonMatches: boolean } | null} */
  let best = null;

  for (const moduleEntry of modules) {
    const commonMatches = pathnameMatchesModule(pathname, moduleEntry);
    for (const child of moduleEntry.children ?? []) {
      if (!pathnameMatchesChildUrl(pathname, child.url)) {
        continue;
      }
      const base = normalizeNavPath(child.url);
      const urlLength = base?.length ?? 0;
      if (!best || urlLength > best.urlLength) {
        best = { moduleKey: moduleEntry.key, childKey: child.key, urlLength, commonMatches };
        continue;
      }
      if (urlLength === best.urlLength && commonMatches && !best.commonMatches) {
        best = { moduleKey: moduleEntry.key, childKey: child.key, urlLength, commonMatches };
      }
    }
  }

  if (best) {
    return { moduleKey: best.moduleKey, childKey: best.childKey };
  }

  const fallback = modules.find(entry => pathnameMatchesModule(pathname, entry));
  return { moduleKey: fallback?.key ?? "", childKey: null };
}

/**
 * Flat list of navigable sidebar destinations for search.
 * @param {Array<{ key: string, label: string, children?: SidebarChild[] }>} modules
 */
export function flattenSidebarDestinations(modules) {
  /** @type {Array<{ moduleKey: string, moduleLabel: string, childKey: string, name: string, url: string, group?: string }>} */
  const rows = [];
  for (const moduleEntry of modules) {
    for (const child of moduleEntry.children ?? []) {
      if (!child.url || child.url === "#" || child.url === "/") {
        continue;
      }
      rows.push({
        moduleKey: moduleEntry.key,
        moduleLabel: moduleEntry.label,
        childKey: child.key,
        name: child.name,
        url: child.url,
        group: child.group,
      });
    }
  }
  return rows;
}
